<template>
    <div class="modal fade show hotel-modal" aria-labelledby="payModalTitle">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center px-1 my-1">
                    <h1 class="mb-0">Payment</h1>
                    <div class="d-flex align-items-center gap-1">
                        <button v-if="showDetails" type="button" class="btn btn-sm btn-outline-primary" @click="$emit('onSeeDetails', reservation)">
                            See Details
                        </button>
                        <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <div class="modal-body">
                    <div class="row g-1 mb-2">
                        <div class="col-4">
                            <div class="summary-card summary-total">
                                <span class="summary-label">Estimate Bill</span>
                                <span class="summary-value">{{ commaFormat(reservation.total) }}</span>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="summary-card summary-paid">
                                <span class="summary-label">Paid</span>
                                <span class="summary-value">{{ commaFormat(reservation.paid) }}</span>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="summary-card summary-due">
                                <span class="summary-label">Due</span>
                                <span class="summary-value">{{ commaFormat(reservation.due) }}</span>
                            </div>
                        </div>
                    </div>

                    <form @submit.prevent="onSubmit">
                        <div class="mb-2">
                            <label class="form-label">Received In</label>
                            <v-select
                                placeholder="Select Cash & Bank Account"
                                v-model="formData.account_head_id"
                                :options="accountHeads"
                                :reduce="head => head.id"
                                label="name"
                            />
                        </div>
                        <div class="mb-2">
                            <label class="form-label">Received Date</label>
                            <input v-model="formData.date" type="date" class="form-control"/>
                        </div>
                        <div class="mb-2">
                            <label class="form-label">Advance Amount</label>
                            <input v-model="formData.amount" type="number" min="0" :max="reservation.due" class="form-control"/>
                        </div>
                        <div class="mb-2">
                            <label class="form-label">Reference</label>
                            <input v-model="formData.reference" type="text" class="form-control"/>
                        </div>

                        <div class="d-flex justify-content-end mt-2">
                            <button type="button" class="btn btn-outline-secondary me-1" @click="closeModal">Cancel</button>
                            <button type="submit" :disabled="loading" class="btn btn-primary">Pay</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleHotel from '@/services/modules/hotel/hotel';
import handleReceipt from '@/services/modules/receipt';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    reservation: {
        type: Object,
        required: true,
    },
    showDetails: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['onSubmit', 'onClose', 'onSeeDetails']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { payHotelReservation } = handleHotel();
const { fetchCashAndBank } = handleReceipt();
const { commaFormat } = figureFormatter();

const loading = ref(false);
const accountHeads = ref([]);
const formData = ref({
    company_id: $route.params.companyId,
    account_head_id: null,
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    reference: '',
});

const closeModal = () => {
    emit('onClose');
};

const validate = () => {
    if (!formData.value.account_head_id) {
        showError('Please select the received account');
        return false;
    }
    if (!formData.value.amount || formData.value.amount <= 0) {
        showError('Please enter a valid amount');
        return false;
    }
    if (Number(formData.value.amount) > Number(props.reservation.due)) {
        showError('Amount cannot exceed due');
        return false;
    }
    return true;
};

const onSubmit = async () => {
    if (!validate()) return;
    loading.value = true;
    await payHotelReservation(formData.value, props.reservation.id).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onSubmit');
        closeModal();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

onMounted(() => {
    fetchCashAndBank($route.params.companyId).then(res => {
        if (res.data) accountHeads.value = res.data;
    });
});
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.65rem 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    height: 100%;
}
.summary-label {
    font-size: 0.72rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    opacity: 0.75;
    margin-bottom: 0.15rem;
}
.summary-value {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.2;
}
.summary-total {
    background: #eef2f7;
    border-color: #dfe6ef;
    color: #384250;
}
.summary-paid {
    background: #e7f6ec;
    border-color: #cdeed8;
    color: #1a7f43;
}
.summary-due {
    background: #fdeceb;
    border-color: #f8d5d2;
    color: #c0392b;
}
</style>
